import React, { useEffect, useRef, useState } from 'react';
import styles from './dropdown-tree-select-custom.module.scss';
import { isEmpty } from 'lodash';
import { POSITION_FINDER_FILTERS, SELECT_ALL_LABEL } from '../../utils/constants';
import { circle_checked, Arrow_Down_2, Arrow_Up_2 } from '../../assets/img';
import { BasicButton } from '../../assets/common';

const DropdownTreeSelectCustom = ({
  data,
  placeholder,
  filterName,
  filterTabActive,
  label,
  setCurrentFilterActive,
  currentFilterActive,
  onApply,
  onBlur,
  clearFilterName,
  setClearFilterName,
}) => {
  const [masterTreeState, setMasterTreeState] = useState(
    data.map((node) => ({
      ...node,
      checked: false,
      children: node?.children ? node.children?.map((child) => ({ ...child, checked: false })) : null,
    }))
  );
  const [filteredTreeState, setFilteredTreeState] = useState(masterTreeState);
  const [searchValue, setSearchValue] = useState('');
  const searchDropdownRef = useRef(null);

  const updateAllChecked = (nodes, isChecked) =>
    nodes.map((node) => ({
      ...node,
      checked: isChecked,
      children: node?.children
        ? node.children?.map((child) => ({
            ...child,
            checked: isChecked,
          }))
        : null,
    }));

  useEffect(() => {
    if (!clearFilterName) return;

    if (clearFilterName === 'ALL' || clearFilterName === filterName) {
      setMasterTreeState(updateAllChecked(masterTreeState, false));
      setFilteredTreeState(updateAllChecked(filteredTreeState, false));
      setClearFilterName(null);
    }
  }, [clearFilterName, setClearFilterName]);

  const getSearchPlaceholder = (key) => {
    let tempLabel = 'Search';
    POSITION_FINDER_FILTERS.forEach((item) => {
      if (item.value === key) {
        tempLabel = item.label;
      }
    });
    return `Search by ${tempLabel}`;
  };

  // Search Handler
  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchValue(value);

    if (value === '') {
      // Reset to full tree when search input is cleared
      setFilteredTreeState(masterTreeState);
    } else {
      const filterTree = (nodes) =>
        nodes
          .map((node) => {
            if (node.label.toLowerCase().includes(value)) {
              return node;
            } else if (node?.children) {
              const filteredChildren = filterTree(node.children);
              if (filteredChildren.length > 0) {
                return { ...node, children: filteredChildren };
              }
            }
            return null;
          })
          .filter(Boolean);

      setFilteredTreeState(filterTree(masterTreeState));
    }
  };

  // Checkbox Handler
  const handleCheckboxChange = (nodeId, isParent, hasChildren) => {
    const updateTree = (nodes) =>
      nodes.map((node) => {
        if (node.value === nodeId && isParent) {
          // Toggle parent checkbox
          return {
            ...node,
            checked: !node.checked,
            children: node?.children
              ? node.children?.map((child) => ({
                  ...child,
                  checked: !node.checked,
                }))
              : null,
          };
        }
        if (hasChildren) {
          // Update child checkbox
          const updatedChildren = node.children?.map((child) => (child.value === nodeId ? { ...child, checked: !child.checked } : child));

          // Check parent if any child is selected
          const anyChildChecked = updatedChildren?.some((child) => child.checked) || false;

          return {
            ...node,
            children: updatedChildren,
            checked: node?.children ? anyChildChecked : node?.checked,
          };
        }
        return node;
      });

    setMasterTreeState(updateTree(masterTreeState));
    setFilteredTreeState(updateTree(filteredTreeState));
  };

  // Select All Handler
  const handleSelectAll = (e) => {
    const isChecked = e.target.checked;
    setMasterTreeState(updateAllChecked(masterTreeState, isChecked));
    setFilteredTreeState(updateAllChecked(filteredTreeState, isChecked));
  };

  const handleClickOutside = (event) => {
    if (searchDropdownRef.current && !searchDropdownRef.current.contains(event.target)) {
      setCurrentFilterActive(false);
      onBlur();
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleApplyFilter = () => {
    onApply(filterName, masterTreeState);
    setFilteredTreeState(masterTreeState);
    setCurrentFilterActive(null);
    setSearchValue('');
  };

  return (
    <div className={styles.dropdownSelectWrapper}>
      <button
        className={`filter_button ${filterTabActive.includes(filterName) ? 'active' : ''}`}
        onClick={() =>
          setCurrentFilterActive((prev) => {
            if (prev !== filterName) {
              return filterName;
            } else {
              return null;
            }
          })
        }
        style={{
          paddingTop: '10px',
          paddingBottom: '10px',
          marginBottom: '10px',
        }}
      >
        {filterTabActive.includes(filterName) && <img src={circle_checked} alt="nofication" />}
        {!filterTabActive.includes(filterName) ? placeholder : label}
      </button>
      {currentFilterActive === filterName && (
        <div className={styles.filterSearchWrapper} ref={searchDropdownRef}>
          {/* Search Input */}
          <input
            className={styles.searchField}
            type="text"
            placeholder={`${getSearchPlaceholder(filterName)}`}
            value={searchValue}
            onChange={handleSearch}
          />

          <div className={styles.optionListWrapper}>
            <div className={styles.scrollList}>
              {!searchValue && (
                <label className={styles.normalItem} style={masterTreeState.some((i) => !isEmpty(i?.children)) ? { marginLeft: '24px' } : {}}>
                  <input
                    type="checkbox"
                    onChange={handleSelectAll}
                    checked={masterTreeState.every((node) =>
                      node?.children ? node.checked && node.children?.every((child) => child.checked) : node.checked
                    )}
                  />
                  <span className="checkmark">{SELECT_ALL_LABEL}</span>
                </label>
              )}

              {/* Tree Structure */}
              <ul className={styles.parentList}>
                {filteredTreeState.map((node) => (
                  <TreeNode key={node.value} node={node} onCheckboxChange={handleCheckboxChange} searchValue={searchValue} />
                ))}
              </ul>
            </div>
            <div className={styles.actionWwapper}>
              <BasicButton onClick={handleApplyFilter} mode="teal">
                Apply
              </BasicButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const TreeNode = ({ node, onCheckboxChange, searchValue }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <li>
      {node?.children && (
        <button className={styles.downUpBtn} onClick={() => setIsExpanded(!isExpanded)}>
          {isExpanded ? <img src={Arrow_Up_2} alt="" /> : <img src={Arrow_Down_2} alt="" />}
        </button>
      )}
      <label className={styles.normalItem}>
        <input type="checkbox" checked={node.checked} onChange={() => onCheckboxChange(node.value, true, node?.children)} />
        <span className="checkmark">{node.label}</span>
      </label>
      {node?.children && (
        <>
          {(isExpanded || searchValue) && (
            <ul className={styles.childList}>
              {node.children.map((child) => (
                <TreeNode key={child.value} node={child} onCheckboxChange={() => onCheckboxChange(child.value, false, true)} />
              ))}
            </ul>
          )}
        </>
      )}
    </li>
  );
};

export default DropdownTreeSelectCustom;
